export type Locale = "en" | "es";

export const SUPPORTED_LOCALES: readonly { code: Locale; label: string }[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];

export type Params = Record<string, string | number>;
type Dictionary = Record<string, string | undefined>;

const catalogs = new Map<Locale, Dictionary>();
/** Wording the club (or the platform) chose over the built-in messages, already merged by the server: club over platform. */
const overrides = new Map<Locale, Dictionary>();

/** Add messages for a locale. Core registers its own; each module registers its screens' messages when its code loads. */
export function registerMessages(locale: Locale, messages: Dictionary): void {
  catalogs.set(locale, { ...catalogs.get(locale), ...messages });
}

/** Every built-in message registered so far for a locale (what the wording catalogue is generated from). */
export function registeredMessages(locale: Locale): Readonly<Dictionary> {
  return catalogs.get(locale) ?? {};
}

/** Replace the wording overrides of a locale (the server's answer for this club); `undefined` or `{}` clears them. */
export function setMessageOverrides(locale: Locale, messages: Dictionary | undefined): void {
  if (messages && Object.keys(messages).length > 0) overrides.set(locale, { ...messages });
  else overrides.delete(locale);
}

/** Best supported locale for a BCP 47 tag such as "es-MX"; undefined if none. */
export function matchLocale(tag: string | null | undefined): Locale | undefined {
  const language = tag?.toLowerCase().split("-")[0];
  return SUPPORTED_LOCALES.find((entry) => entry.code === language)?.code;
}

/** Whether a message exists (in this locale or English); for keys built at run time such as error codes. */
export function hasMessage(locale: Locale, key: string): boolean {
  return lookup(locale, key) !== undefined;
}

/** The message for a key. Order: this language's override, its built-in message, then the same two in English. */
function lookup(locale: Locale, key: string): string | undefined {
  return (
    overrides.get(locale)?.[key] ??
    catalogs.get(locale)?.[key] ??
    (locale === "en" ? undefined : (overrides.get("en")?.[key] ?? catalogs.get("en")?.[key]))
  );
}

// ---- "Edit text on screen": in edit mode a translated string ends with an invisible tag that names its key.
// The tag is a word joiner, the key's number in zero-width bits, and another word joiner. Overrides cannot contain these characters
// (the server refuses invisible characters), so a tag is never mistaken for wording.

const TAG_EDGE = "\u2060";
const BIT_ZERO = "\u200b";
const BIT_ONE = "\u200c";
const tagKeys: string[] = [];
const tagIds = new Map<string, number>();

/** The invisible tag for a key; appended to a string in edit mode. */
export function textTag(key: string): string {
  let id = tagIds.get(key);
  if (id === undefined) {
    id = tagKeys.push(key) - 1;
    tagIds.set(key, id);
  }
  const bits = (id + 1).toString(2).slice(1) || "";
  return TAG_EDGE + bits.replace(/0/g, BIT_ZERO).replace(/1/g, BIT_ONE) + TAG_EDGE;
}

/** Whether a string carries at least one tag (cheap check before decoding). */
export function hasTextTag(value: string | null | undefined): boolean {
  return !!value && value.includes(TAG_EDGE);
}

/** The keys named by the tags in a string, in order, without repeats. */
export function textTagKeys(value: string): string[] {
  const found: string[] = [];
  for (const match of value.matchAll(/\u2060([\u200b\u200c]*)\u2060/g)) {
    const bits = (match[1] ?? "").replace(new RegExp(BIT_ZERO, "g"), "0").replace(new RegExp(BIT_ONE, "g"), "1");
    const key = tagKeys[Number.parseInt(`1${bits}`, 2) - 1];
    if (key !== undefined && !found.includes(key)) found.push(key);
  }
  return found;
}

/** The string without its tags. */
export function stripTextTags(value: string): string {
  return value.replace(/\u2060[\u200b\u200c]*\u2060/g, "");
}

export interface TranslateOptions {
  /** Edit mode: end the text with an invisible tag naming the key it came from. */
  tag?: boolean;
}

/**
 * Looks up `key`. When `params.count` is a number the plural form `key_one` / `key_other` (as
 * chosen by Intl.PluralRules for the locale) is used. `{name}` placeholders are replaced.
 * A missing key falls back to English, then to the key itself so gaps are visible, not blank.
 * Wording overrides win over the built-in message of the same language.
 */
export function translate(locale: Locale, key: string, params?: Params, options?: TranslateOptions): string {
  let template: string | undefined;
  let found = key;
  if (typeof params?.count === "number") {
    const category = new Intl.PluralRules(locale).select(params.count);
    found = lookup(locale, `${key}_${category}`) !== undefined ? `${key}_${category}` : `${key}_other`;
    template = lookup(locale, found);
  }
  if (template === undefined) {
    found = key;
    template = lookup(locale, key);
  }
  const text = (template ?? key).replace(/\{(\w+)\}/g, (whole, name: string) => (params && name in params ? String(params[name]) : whole));
  return options?.tag && template !== undefined ? text + textTag(found) : text;
}
